import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { routes } from '../../routes/AppRoutes';
import { strings } from '../../resources/strings';
import { SessionHelper } from '../../helpers/SessionHelper';
import { MainActions } from '../../actions/MainActions';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { GetOrderPendingInvoicingHistoryListInput } from '../../services/OrderPendingInvoicingHistories/GetOrderPendingInvoicingHistoryList/GetOrderPendingInvoicingHistoryListInput';
import { OrderPendingInvoicingHistoryItem } from '../../services/OrderPendingInvoicingHistories/GetOrderPendingInvoicingHistoryList/GetOrderPendingInvoicingHistoryListOutput';
import { Header } from '../../common-components/Layout/Header/Header';
import { IBreadcrumbItem } from '../../common-components/Breadcrumb/Breadcrumb';
import { SectionsPage } from '../../common-components/Layout/SectionsPage/SectionsPage';
import { ActionsBox } from '../../common-components/Layout/ActionsBox/ActionsBox';
import { FlexColumnListHeader } from '../../common-components/Layout/FlexColumnList/FlexColumnListHeader';
import { FlexColumnListRow } from '../../common-components/Layout/FlexColumnList/FlexColumnListRow';
import { FlexColumnListFooter } from '../../common-components/Layout/FlexColumnList/FlexColumnListFooter';
import { IFlexColumnListItem } from '../../common-components/Layout/FlexColumnList/IFlexColumnListItem';
import { IActionsBoxItem } from '../../common-components/Layout/ActionsBox/IActionsBoxItem';
import { If } from '../../common-components/If/If';
import {
  IFilterToListPendingInvoicingOrdersOutput,
  ModalFiltersToListPendingInvoicingOrders,
  PendingInvoicingDeliveryFilter,
  PendingInvoicingOrderSort,
  PendingInvoicingSituationFilter
} from './ModalFiltersToListPendingInvoicingOrders/ModalFiltersToListPendingInvoicingOrders';
import { AppliedFiltersToListPendingInvoicingOrders } from './AppliedFiltersToListPendingInvoicingOrders/AppliedFiltersToListPendingInvoicingOrders';
import { ModalPendingInvoicingOrderDetails } from './ModalPendingInvoicingOrderDetails/ModalPendingInvoicingOrderDetails';
import { ColumnHint } from './styles';
import {
  hints,
  formatMinutes,
  getChannelText,
  getDeliveryText,
  getReasonText,
  getSeparationText,
  getSituationText
} from './pendingInvoicingOrderUi';

const ActionsKey = {
  Update: 'update',
  Filter: 'filter',
  ClearFilter: 'clear_filter'
};

const actions: IActionsBoxItem[] = [
  {
    key: ActionsKey.Update,
    text: 'Atualizar'
  },
  {
    key: ActionsKey.Filter,
    text: strings.Filter
  },
  {
    key: ActionsKey.ClearFilter,
    text: 'Limpar filtros',
    minWidth: '120px'
  }
];

const columns: IFlexColumnListItem[] = [
  {
    field: 'situationIcon',
    title: <ColumnHint title={hints.situation}>Sit.</ColumnHint>,
    width: '45px',
    align: 'center'
  },
  {
    field: 'number',
    title: 'Pedido',
    width: '90px'
  },
  {
    field: 'paidAt',
    title: 'Pago em',
    width: '150px',
    type: 'datetime'
  },
  {
    field: 'timeText',
    title: <ColumnHint title={hints.time}>Tempo</ColumnHint>,
    width: '90px',
    align: 'center'
  },
  {
    field: 'pendingEntriesCount',
    title: <ColumnHint title={hints.entries}>Vezes</ColumnHint>,
    width: '60px',
    align: 'center'
  },
  {
    field: 'stateDescription',
    title: <ColumnHint title={hints.state}>Estado</ColumnHint>,
    width: '180px'
  },
  {
    field: 'separationText',
    title: 'Tipo',
    width: '90px'
  },
  {
    field: 'deliveryText',
    title: 'Entrega',
    width: '130px'
  },
  {
    field: 'channelText',
    title: 'Canal',
    width: '110px'
  },
  {
    field: 'reasonText',
    title: <ColumnHint title={hints.reason}>Motivo (Quatro Olhos)</ColumnHint>,
    width: 'calc(100% - 1012px)',
    widthRow: 'calc(100% - 1010px)'
  },
  {
    field: 'genericalOptionsRow',
    title: 'Opções',
    width: '65px',
    align: 'center',
    type: 'options'
  },
  {
    field: '_',
    title: '',
    width: '2px'
  }
];

const GenericalOptionsRow = {
  Details: 'details'
};

const defaultFilter: IFilterToListPendingInvoicingOrdersOutput = {
  situation: PendingInvoicingSituationFilter.Pending
};

interface IPendingInvoicingOrderRow extends OrderPendingInvoicingHistoryItem {
  situationIcon: React.ReactNode;
  timeText: string;
  separationText: string;
  deliveryText: string;
  channelText: string;
  reasonText: string;
}

const ManagePendingInvoicingOrders: React.FC = () => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const navigate = useNavigate();
  const { session, signOut } = useAuth();

  const {
    getOrderPendingInvoicingHistoryListService,
    toastService
  } = React.useContext(ServicesContext);

  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = React.useState<number>(0);
  const [amountOfPages, setAmountOfPages] = React.useState<number>(0);
  const [totalAmount, setTotalAmount] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [filtering, setFiltering] = React.useState<boolean>(true);
  const [appliedFilter, setAppliedFilter] = React.useState<IFilterToListPendingInvoicingOrdersOutput>(defaultFilter);
  const [rows, setRows] = React.useState<IPendingInvoicingOrderRow[] | undefined>(undefined);
  const [modalFilterVisible, setModalFilterVisible] = React.useState<boolean>(false);
  const [detailsItem, setDetailsItem] = React.useState<OrderPendingInvoicingHistoryItem | undefined>(undefined);

  const breadcrumbItems: IBreadcrumbItem[] = React.useMemo(() => [
    {
      text: strings.Home,
      route: routes.Home
    }
  ], []);

  const optionGroups = React.useMemo(() => [
    {
      key: 'genericalOptionsRow',
      options: [
        { key: GenericalOptionsRow.Details, text: 'Detalhar' }
      ],
      click: genericalOptionsRowHandle
    }
  ], []);

  useEffect(() => {
    if (!session || SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }

    filter(defaultFilter, pageSize, 1);
  }, [session, navigate]);

  return (
    <>
      <SectionsPage
        loading={filtering}
        header={{
          padding: '0px 63px',
          backgroundColor: '#fff',
          items: [
            <Header
              key={'header'}
              title={strings.ManagePendingInvoicingOrders}
              breadcrumbItems={breadcrumbItems}
            />
          ]
        }}
        body={{
          padding: '0px 63px',
          backgroundColor: 'transparent',
          header: [
            <AppliedFiltersToListPendingInvoicingOrders
              key={'applied-filters'}
              appliedFilters={appliedFilter}
            />,
            <ActionsBox
              key={'actions-box'}
              defaultItemWidth='80px'
              actions={actions}
              click={actionBoxClick}
              loading={filtering}
            />,
            <FlexColumnListHeader
              name='pending_invoicing_orders'
              columns={columns}
              loading={filtering}
            />
          ]
        }}
        footer={{
          padding: '10px 63px',
          backgroundColor: '#fff',
          items: [
            <FlexColumnListFooter
              key={'pending_invoicing_orders_footer'}
              currentPage={currentPage}
              currentPageSize={currentPageSize}
              amountOfPages={amountOfPages}
              totalAmount={totalAmount}
              pageSize={pageSize}
              changePageSize={changePageSize}
              changePage={changePage}
              loading={filtering}
            />
          ]
        }}
      >
        <FlexColumnListRow
          name='pending_invoicing_orders'
          columns={columns}
          values={rows}
          loading={filtering}
          optionGroups={optionGroups}
        />
      </SectionsPage>

      <If condition={modalFilterVisible}>
        <ModalFiltersToListPendingInvoicingOrders
          appliedFilters={appliedFilter}
          close={() => setModalFilterVisible(false)}
          finish={(values) => {
            setModalFilterVisible(false);
            filter(values, pageSize, 1);
          }}
        />
      </If>

      <If condition={!!detailsItem}>
        {detailsItem ? (
          <ModalPendingInvoicingOrderDetails
            item={detailsItem}
            close={() => setDetailsItem(undefined)}
          />
        ) : null}
      </If>
    </>
  );

  function changePage(page: number) {
    filter(appliedFilter, pageSize, page);
  }

  function changePageSize(size: number) {
    setPageSize(size);
    filter(appliedFilter, size, 1);
  }

  function filter(values: IFilterToListPendingInvoicingOrdersOutput, size: number, page: number) {
    if (SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }

    mainActions.setNotWait(true);
    setFiltering(true);
    setAppliedFilter(values);
    setCurrentPage(page);

    const filterInput = new GetOrderPendingInvoicingHistoryListInput();
    filterInput.pending = values.situation === PendingInvoicingSituationFilter.All
      ? undefined
      : values.situation === PendingInvoicingSituationFilter.Pending;
    filterInput.number = values.number;
    filterInput.stateId = values.stateId;
    filterInput.express = !values.delivery || values.delivery === PendingInvoicingDeliveryFilter.All
      ? undefined
      : values.delivery === PendingInvoicingDeliveryFilter.Express;
    filterInput.newestFirst = values.sort !== PendingInvoicingOrderSort.OldestFirst;
    filterInput.page = page;
    filterInput.pageSize = size > 0 ? size : 25;

    getOrderPendingInvoicingHistoryListService.handle(filterInput)
      .then(result => {
        const output = result?.value;
        const items = output?.items || [];
        const total = output?.total || 0;
        const currentSize = output?.pageSize || filterInput.pageSize || 25;
        setRows(items.map(toRow));
        setCurrentPage(output?.page || page);
        setCurrentPageSize(items.length);
        setAmountOfPages(Math.ceil(total / currentSize));
        setTotalAmount(total);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setFiltering(false);
      });
  }

  function toRow(item: OrderPendingInvoicingHistoryItem): IPendingInvoicingOrderRow {
    return {
      ...item,
      situationIcon: (
        <span title={getSituationText(item)}>
          <FontAwesomeIcon
            icon={item.isPending ? faHourglassHalf : faCheckCircle}
            color={item.isPending ? '#f0ad4e' : '#198754'}
          />
        </span>
      ),
      timeText: formatMinutes(item.minutesSincePayment),
      separationText: getSeparationText(item),
      deliveryText: getDeliveryText(item),
      channelText: getChannelText(item),
      reasonText: getReasonText(item)
    };
  }

  function actionBoxClick(key: string) {
    if (key === ActionsKey.Update) {
      filter(appliedFilter, pageSize, currentPage);
      return;
    }
    if (key === ActionsKey.Filter) {
      setModalFilterVisible(true);
      return;
    }
    if (key === ActionsKey.ClearFilter) {
      filter(defaultFilter, pageSize, 1);
      return;
    }
  }

  function genericalOptionsRowHandle(key: string, row: IPendingInvoicingOrderRow) {
    if (key === GenericalOptionsRow.Details) {
      setDetailsItem(row);
      return;
    }
  }
}

export default ManagePendingInvoicingOrders;
